import React from 'react';
import { PropTypes } from 'prop-types';
import AutocompleteText from './AutocompleteText';
import { generateComponentDocs, generateStoryDocs } from '../../../utils/documentationUtils';

export default {
  title: 'Components/Inputs/AutocompleteText',
  component: AutocompleteText,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `AutocompleteText` component provides an input field with autocomplete functionality. It is designed to enhance user experience by offering selectable options while typing.\n\n',
      // When & how to use it?
      '\nUse the `AutocompleteText` component in forms or search functionalities where users need to input text with optional predefined suggestions. It supports search, clear, and custom styling options.\n\n',
      // Current Usage
      `\nThe \`AutocompleteText\` component is commonly used in scenarios like:
      \n- Search Forms
      \n- Filter Inputs
      \n- Dropdown Selections with Search Capabilities\n\n`,
      // Related Components
      `\n- **[CustomSelect](http://localhost:6006/?path=/docs/components-inputs-customselect--docs)**: Similar dropdown component without search functionality.
       \n- **[TextInput](http://localhost:6006/?path=/docs/components-inputs-textinput--docs)**: Simple text input field for forms.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    label: {
      control: { type: 'text' },
      description: 'Label for the input field displayed above the dropdown.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    value: {
      control: { type: 'object' },
      description: 'The selected value of the input field, represented as an object.\n\n',
      table: {
        type: { summary: 'object' },
        required: false,
      },
    },
    onChange: {
      control: { type: 'function' },
      description: 'Callback function triggered when the selected value changes.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    options: {
      control: { type: 'array' },
      description: 'Array of selectable options displayed in the dropdown. Each option should be an object with a `label` and `value` property.\n\n',
      table: {
        type: { summary: 'array' },
        required: true,
      },
    },
  },
};

const DEFAULT_OPTIONS = [];

const AutocompleteTextTemplate = ({
  label,
  value,
  onChange = () => {},
  options = DEFAULT_OPTIONS,
}) => {
  const [selectedValue, setSelectedValue] = React.useState(value);
  return (
    <div style={{ minHeight: '400px' }}>
      <AutocompleteText
        label={label}
        value={selectedValue}
        onChange={(option) => {
          setSelectedValue(option);
          onChange(option);
        }}
        options={options}
      />
    </div>
  );
};

const Template = AutocompleteTextTemplate.bind({});

export const Default = Template.bind({});
Default.args = {
  label: 'Autocomplete Field',
  options: [
    { label: 'Option 1', value: 'option1' },
    { label: 'Option 2', value: 'option2' },
    { label: 'Option 3', value: 'option3' },
  ],
};
Default.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used to demonstrate the default configuration of the `AutocompleteText` component with a set of sample options.\n\n',
  // Where is it currently used on the product?
  '\nThe `AutocompleteText` component currently is used in 2 search components for fields requiring text input with suggestions.\n\n',
);

export const PreselectedValue = Template.bind({});
PreselectedValue.args = {
  label: 'Preselected Autocomplete',
  value: { label: 'Option 2', value: 'option2' },
  options: [
    { label: 'Option 1', value: 'option1' },
    { label: 'Option 2', value: 'option2' },
    { label: 'Option 3', value: 'option3' },
  ],
};
PreselectedValue.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used when the `AutocompleteText` field is initialized with a preselected value.\n\n',
  // Where is it currently used on the product?
  '\nThe `AutocompleteText` component in this variant is commonly used in forms where default values need to be displayed.\n\n',
);

AutocompleteTextTemplate.propTypes = {
  label: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })),
};
